import _ from 'lodash';
import md5 from 'blueimp-md5';
import io from 'socket.io-client';
import { history, matchPath } from 'umi';
import tokenEvents, { TOKEN_EVENTS } from './tokenEvents';
import { getSessionToken } from '../utils/infrastructure/auth';
import { parseJson } from '../utils/infrastructure/utils';

class SocketService {
  constructor() {
    if (SocketService._instance) {
      return SocketService._instance;
    }

    this._initializeProperties();
    this._setupTokenEventListeners();
    this._setupLifecycleListeners();
    this._startJoiningGroupsCleanup();
    SocketService._instance = this;
  }

  static getInstance() {
    if (!SocketService._instance) {
      SocketService._instance = new SocketService();
    }
    return SocketService._instance;
  }

  get connected() {
    return this.isConnected;
  }

  get token() {
    return this._getTokenFromOptions();
  }

  _initializeProperties() {
    this.socket = null;
    this.currentGroup = null;
    this.isConnected = false;
    this.messageQueue = new Map();
    this.joiningGroups = new Map();
    this.eventListeners = new Map();
    this.initialized = false;
    this.serverUrl = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 15;
    this.isReconnecting = false;
    this.reconnectDelay = 2000;
    this.joiningGroupsCleanupInterval = null;
    this.joiningGroupsCleanupIntervalMs = 5000;
    this.joiningGroupsInFlightTtl = 15000;
    this._heartbeatInterval = null;
    this._delayedReconnectTimer = null;
  }

  /**
   * Setup listeners for token refresh events
   */
  _setupTokenEventListeners() {
    // Listen for token refresh events
    tokenEvents.on(TOKEN_EVENTS.TOKEN_REFRESHED, this._handleTokenRefresh.bind(this));
    tokenEvents.on(TOKEN_EVENTS.SOCKET_RECONNECT_NEEDED, this._handleReconnectRequest.bind(this));
  }

  /**
   * Setup page lifecycle listeners to proactively restore socket connection
   * after the app returns to foreground or network becomes online
   */
  _setupLifecycleListeners() {
    try {
      // Debounce reconnect attempts to prevent storms from rapid visibility/online changes
      this._lifecycleReconnectTimer = null;
      const debouncedReconnect = () => {
        if (this._lifecycleReconnectTimer) clearTimeout(this._lifecycleReconnectTimer);
        this._lifecycleReconnectTimer = setTimeout(() => {
          this._lifecycleReconnectTimer = null;
          this.ensureConnected();
        }, 300);
      };

      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          try {
            if (document.visibilityState === 'visible') {
              debouncedReconnect();
            }
          } catch (e) {}
        });
      }

      if (typeof window !== 'undefined') {
        window.addEventListener('online', () => {
          try {
            debouncedReconnect();
          } catch (e) {}
        });
      }
    } catch (e) {}
  }

  _startJoiningGroupsCleanup() {
    if (this.joiningGroupsCleanupInterval) return;
    this.joiningGroupsCleanupInterval = setInterval(() => {
      try {
        this._cleanupJoiningGroups();
      } catch (e) {
        if (process.env.NODE_ENV === 'development')
          console.warn('[SocketService] joiningGroups cleanup error:', e);
      }
    }, this.joiningGroupsCleanupIntervalMs);
  }

  _cleanupJoiningGroups() {
    const now = Date.now();
    this.joiningGroups.forEach((entry, key) => {
      const { joining, disconnecting, timestamp } = entry || {};
      if (
        (joining || disconnecting) &&
        timestamp &&
        now - timestamp > this.joiningGroupsInFlightTtl
      ) {
        this.joiningGroups.delete(key);
      }
    });
  }

  _normalizeJoinKey(key) {
    if (!key && key !== 0) return null;
    return key.toString().toUpperCase();
  }

  _markJoiningGroupEntry(key, entry) {
    const normalizedKey = this._normalizeJoinKey(key);
    if (!normalizedKey) return;
    const existing = this.joiningGroups.get(normalizedKey) || {};
    this.joiningGroups.set(normalizedKey, {
      ...existing,
      ...entry,
      data: entry?.data || existing.data,
      timestamp: Date.now(),
    });
  }

  _releaseJoiningGroupEntry(key) {
    const normalizedKey = this._normalizeJoinKey(key);
    if (!normalizedKey) return;
    this.joiningGroups.delete(normalizedKey);
  }

  _isJoinEvent(event, data) {
    return (
      event === 'joinChat' ||
      event === 'joinGroup' ||
      data?.type === 'joinChat' ||
      data?.type === 'joinGroup'
    );
  }

  _isLeaveEvent(event, data) {
    return event === 'leaveGroup' || data?.type === 'leaveChat' || data?.type === 'leaveGroup';
  }

  /**
   * Periodic heartbeat to detect and recover dead connections.
   * If socket is disconnected and not already reconnecting, triggers reconnection.
   */
  _startHeartbeat() {
    this._stopHeartbeat();
    this._heartbeatInterval = setInterval(() => {
      try {
        if (this.socket && this.socket.connected) return;
        if (!this.serverUrl || this.isReconnecting) return;
        const token = this._getTokenFromOptions();
        if (token) {
          if (this.socket && this.socket.auth) {
            this.socket.auth.token = token;
          }
          // If the socket exists but is not connected, try calling connect()
          // which resumes socket.io's built-in reconnection with updated auth.
          if (this.socket && !this.socket.connected) {
            try {
              this.socket.connect();
            } catch (e) {}
          } else if (!this.socket) {
            this._attemptReconnectWithToken(token).catch(() => {});
          }
        }
      } catch (e) {}
    }, 30000);
  }

  _stopHeartbeat() {
    if (this._heartbeatInterval) {
      clearInterval(this._heartbeatInterval);
      this._heartbeatInterval = null;
    }
  }

  /**
   * Public API: ensures the socket is connected.
   * If disconnected, updates auth token and kicks off reconnection.
   * Returns true if currently connected.
   */
  ensureConnected() {
    if (this.socket && this.socket.connected) return true;
    if (!this.serverUrl) return false;

    const token = this._getTokenFromOptions();

    if (this.socket) {
      // Update auth so next handshake uses fresh token
      if (token && this.socket.auth) {
        this.socket.auth.token = token;
      }
      // Resume socket.io built-in reconnection
      if (!this.socket.connected) {
        try {
          this.socket.connect();
        } catch (e) {}
      }
    } else if (token && !this.isReconnecting) {
      // No socket instance at all — create fresh
      this._attemptReconnectWithToken(token).catch(() => {});
    }

    return this.isConnected;
  }

  /**
   * Handle token refresh event.
   * Updates auth on the existing socket so that socket.io's built-in reconnection
   * uses the fresh token. Avoids destroying the socket unnecessarily.
   */
  async _handleTokenRefresh(data) {
    const { token } = data;
    if (!this.serverUrl) return;

    if (this.socket && this.socket.auth) {
      this.socket.auth.token = token;
    }

    if (this.socket && !this.socket.connected) {
      // Socket exists but is disconnected — resume connection with updated auth.
      try {
        this.socket.connect();
      } catch (e) {}
    } else if (!this.socket && token) {
      // Socket was destroyed — recreate
      await this._attemptReconnectWithToken(token);
    }
    // If already connected, no action needed; connection continues with current session.
  }

  /**
   * Handle reconnection request (e.g. after token_refresh).
   *
   * IMPORTANT: when the reason is `token_refresh` (or any explicit force),
   * we must cycle the socket even if it is currently connected. The JWT used
   * by the server is the one captured at the WebSocket *handshake*; simply
   * mutating `socket.auth.token` on an already-connected socket does NOT
   * upgrade the server-side session, so subsequent emits keep being rejected
   * with 401 until the connection is re-established.
   *
   * For other reasons we keep the previous non-destructive behavior.
   */
  async _handleReconnectRequest(data) {
    if (!this.serverUrl) return;

    const token = this._getTokenFromOptions() || this.token;
    const reason = data?.reason;
    const requiresHandshakeRefresh = reason === 'token_refresh' || reason === 'force';

    if (this.socket) {
      if (token && this.socket.auth) {
        this.socket.auth.token = token;
      }

      if (this.socket.connected) {
        if (requiresHandshakeRefresh && token) {
          await this._attemptReconnectWithToken(token);
        }
        return;
      }

      try {
        this.socket.connect();
      } catch (e) {}
    } else if (token) {
      await this._attemptReconnectWithToken(token);
    }
  }

  /**
   * Attempt to reconnect with new token
   * @param {string} newToken - New access token
   */
  async _attemptReconnectWithToken(newToken) {
    if (this.isReconnecting) return;

    this.isReconnecting = true;
    this.reconnectAttempts = 0;

    try {
      await this._performReconnection(newToken);

      // Emit success event
      tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECTED, {
        timestamp: Date.now(),
        attempts: this.reconnectAttempts + 1,
      });
    } catch (error) {
      // Emit failure event
      tokenEvents.emit(TOKEN_EVENTS.SOCKET_RECONNECT_FAILED, {
        error,
        attempts: this.reconnectAttempts + 1,
        timestamp: Date.now(),
      });

      // Don't give up forever — schedule a delayed retry so the heartbeat or
      // a later ensureConnected() call can pick it up.
      this._scheduleDelayedReconnect();
    } finally {
      this.isReconnecting = false;
    }
  }

  /**
   * Schedules a one-shot reconnection attempt after a cooldown period.
   * Prevents permanent "dead socket" state after max retries are exhausted.
   */
  _scheduleDelayedReconnect() {
    if (this._delayedReconnectTimer) return;
    this._delayedReconnectTimer = setTimeout(() => {
      this._delayedReconnectTimer = null;
      if (this.isConnected || !this.serverUrl) return;
      const token = this._getTokenFromOptions();
      if (token && !this.isReconnecting) {
        this._attemptReconnectWithToken(token).catch(() => {});
      }
    }, 30000);
  }

  /**
   * Perform the actual reconnection with retry logic
   * @param {string} newToken - New access token
   */
  async _performReconnection(newToken) {
    while (this.reconnectAttempts < this.maxReconnectAttempts) {
      try {
        // Disconnect current socket
        if (this.socket) {
          this.socket.disconnect();
        }

        // Wait a bit before reconnecting
        if (this.reconnectAttempts > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1)),
          );
        }

        // Attempt reconnection
        this.socket = io(this.serverUrl, {
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          auth: { token: newToken },
          transports: ['websocket'],
          path: '/ws',
        });

        // Setup events for new socket
        // Reset initialized flag to configure listeners for the new socket instance
        this.initialized = false;
        this._setupSocketEvents();
        this._startJoiningGroupsCleanup();
        // Rebind any external listeners registered via SocketService.on(...)
        this._rebindEventListeners();

        // Wait for connection
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Connection timeout'));
          }, 10000);

          this.socket.once('connect', () => {
            clearTimeout(timeout);
            resolve();
          });

          this.socket.once('connect_error', (error) => {
            clearTimeout(timeout);
            reject(error);
          });
        });

        // Success - break out of retry loop
        this.reconnectAttempts = 0;
        this._startHeartbeat();
        return;
      } catch (error) {
        this.reconnectAttempts++;

        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          throw new Error(
            `Failed to reconnect after ${this.maxReconnectAttempts} attempts: ${error.message}`,
          );
        }
      }
    }
  }

  _getTokenFromOptions() {
    return this._readStoredToken()?.accessToken || null;
  }

  /**
   * Returns the full stored token object (accessToken, expiresIn, issuedAt, ...)
   * for the current session or external URL context. Used by callers that need
   * expiration metadata, not just the bearer string.
   */
  _readStoredToken() {
    let tokenStoredKey = 'token';

    const pathname = _.get(history.location, 'pathname');
    const externalUrlMatch = matchPath(pathname, { path: '/external/', exact: false });

    if (externalUrlMatch) {
      const { publicCode, code } = history.location.query || {};
      tokenStoredKey = publicCode ? `publicToken"${publicCode}"` : `externalToken"${md5(code)}"`;
    }

    try {
      return externalUrlMatch
        ? parseJson(localStorage.getItem(`x-auth-${tokenStoredKey}`))
        : getSessionToken();
    } catch (e) {
      return null;
    }
  }

  /**
   * Returns the time (in ms) until the stored token expires. Negative means
   * already expired. `Infinity` means no expiration metadata available
   * (treat as valid to avoid false positives).
   */
  _getTokenTimeToExpiryMs() {
    const stored = this._readStoredToken();
    if (!stored || !stored.expiresIn) return Infinity;
    const issuedAt = stored.issuedAt || Date.now();
    const expiresAt = issuedAt + stored.expiresIn * 1000;
    return expiresAt - Date.now();
  }

  /**
   * Returns true when the stored token is already expired or will expire
   * within `bufferMs` (default 30s). Use to decide whether a request that
   * goes through the socket must wait for a refresh before being emitted.
   */
  isTokenExpiredOrExpiring(bufferMs = 30000) {
    const ttl = this._getTokenTimeToExpiryMs();
    if (ttl === Infinity) return false; // unknown → don't block
    return ttl <= bufferMs;
  }

  /**
   * Ensures the auth token is fresh AND the socket session uses it before
   * performing a sensitive operation (e.g. sending a chat message).
   *
   * The server-side JWT is tied to the WebSocket handshake, so a token
   * refresh alone is not enough: we must also wait for the socket to
   * reconnect with the new credentials (driven by `_handleReconnectRequest`
   * when reason === 'token_refresh').
   *
   * Returns true when the token is fresh AND the socket is connected.
   * Returns false on timeout or refresh failure — the caller may still try
   * the operation; failures will be caught by the 401 detection in
   * `_emitMessage`, which schedules a retry.
   */
  async ensureValidToken({ bufferMs = 30000, timeoutMs = 10000 } = {}) {
    const tokenIsFresh = () => !this.isTokenExpiredOrExpiring(bufferMs);
    const socketIsLive = () => Boolean(this.socket && this.socket.connected);

    if (tokenIsFresh() && socketIsLive()) return true;

    return new Promise((resolve) => {
      let settled = false;
      let tokenReady = tokenIsFresh();

      const cleanup = () => {
        unsubscribeRefreshed();
        unsubscribeFailed();
        unsubscribeReconnected();
        unsubscribeReconnectFailed();
        clearTimeout(timer);
      };

      const finish = (ok) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(ok);
      };

      const maybeResolve = () => {
        if (tokenReady && socketIsLive()) finish(true);
      };

      const unsubscribeRefreshed = tokenEvents.on(TOKEN_EVENTS.TOKEN_REFRESHED, () => {
        if (tokenIsFresh()) {
          tokenReady = true;
          maybeResolve();
        }
      });
      const unsubscribeFailed = tokenEvents.on(TOKEN_EVENTS.TOKEN_REFRESH_FAILED, () =>
        finish(false),
      );
      const unsubscribeReconnected = tokenEvents.on(TOKEN_EVENTS.SOCKET_RECONNECTED, () =>
        maybeResolve(),
      );
      const unsubscribeReconnectFailed = tokenEvents.on(TOKEN_EVENTS.SOCKET_RECONNECT_FAILED, () =>
        finish(false),
      );

      const timer = setTimeout(() => finish(false), timeoutMs);

      // If the token is already fresh but the socket is still mid-handshake
      // (e.g. another path just refreshed), just wait for SOCKET_RECONNECTED.
      if (tokenReady) {
        // Nudge the socket layer in case it needs help resuming.
        this.ensureConnected();
        return;
      }

      // Otherwise kick off the refresh via the SOCKET_AUTH_ERROR channel,
      // which `useTokenRefresh` listens to. Reuses the proven flow used
      // when the server returns 401 during an emit.
      tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
        error: { message: 'token_expiring', reason: 'pre_emit_check' },
        timestamp: Date.now(),
        socketService: this,
      });
    });
  }

  /**
   *
   * @param {String} serverUrl
   * @param {[String]} uToken
   */
  init(serverUrl, uToken = null) {
    // Store server URL for reconnections
    this.serverUrl = serverUrl;

    // Si ya hay un socket conectado con la misma URL, no crear otro
    if (this.socket && this.socket.connected && this.socket.io.uri === serverUrl) {
      return;
    }

    // If there's a socket but not connected or different URL, disconnect first
    if (this.socket) {
      this.disconnect();
    }

    const token = uToken || this._getTokenFromOptions();

    this.socket = io(serverUrl, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      auth: { token },
      transports: ['websocket'],
      path: '/ws',
    });

    this._setupSocketEvents();
    this._startJoiningGroupsCleanup();
    this._rebindEventListeners();
    this._startHeartbeat();
  }

  _setupSocketEvents() {
    // Verify socket exists and is not already configured
    if (!this.socket || this.initialized) return;

    this.initialized = true;

    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
      this._processQueue();
      // Restore previously joined groups/rooms immediately
      this._restoreJoins();
      // Notify consumers so they can re-register listeners that were cleared
      // by a destructive disconnect() + init() cycle (_offAll clears the Map).
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('socketServiceConnected'));
        }
      } catch (e) {}
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      try {
        if (typeof window !== 'undefined') window.__supportChatCanSend = false;
      } catch (e) {}
      // Keep joins state; upon reconnect, _restoreJoins will re-emit
    });

    this.socket.on('reconnect', (attempt) => {
      this.isConnected = true;
      this.reconnectAttempts = 0; // Reset reconnect attempts
      this._processQueue();
      // Restore previously joined groups/rooms immediately
      this._restoreJoins();
    });

    this.socket.on('connect_error', (err) => {
      this.isConnected = false;

      // Check if it's an authentication error (401)
      if (
        err?.message === 'Authentication error' ||
        err?.data?.statusCode === 401 ||
        err?.type === 'AuthenticationError' ||
        (err?.message && err.message.includes('401'))
      ) {
        // Emit token event for authentication errors
        tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
          error: err,
          timestamp: Date.now(),
          socketService: this,
        });

        // Also dispatch custom event for backward compatibility
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('socketAuthError', {
              detail: { error: err, socketService: this },
            }),
          );
        }
      }
      // If network is back later or visibility changes, lifecycle listeners will trigger reconnection
    });

    // Global listener to detect invalid_token messages from any channel
    // and trigger the authentication error flow
    if (typeof this.socket.onAny === 'function') {
      this.socket.onAny((event, data) => {
        try {
          if (data && typeof data === 'object' && data.message === 'invalid_token') {
            tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
              error: { message: 'invalid_token', event },
              timestamp: Date.now(),
              socketService: this,
            });

            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('socketAuthError', {
                  detail: { error: { message: 'invalid_token', event }, socketService: this },
                }),
              );
            }
          }
        } catch (e) {
          // Silent fail
        }
      });
    }
  }

  /**
   * Rebind previously registered external event listeners to the current socket instance
   */
  _rebindEventListeners() {
    if (!this.socket || !this.eventListeners || this.eventListeners.size === 0) return;
    this.eventListeners.forEach((callbacks, event) => {
      if (callbacks && callbacks.forEach) {
        callbacks.forEach((cb) => {
          try {
            this.socket.on(event, cb);
          } catch (e) {}
        });
      }
    });
  }

  /**
   * Re-emit join messages for previously joined groups/rooms after reconnection
   */
  _restoreJoins() {
    if (!this.socket) return;
    try {
      this.joiningGroups.forEach((entry, key) => {
        const payload = entry?.data;
        // Force event to be a join operation; never replay chatMessage or others
        const eventName = payload?.threadId ? 'joinChat' : 'joinGroup';
        if (payload) {
          this._emitMessage(eventName, payload, null, null);
        }
      });
    } catch (e) {}
  }

  send(event, data, onSuccess, onError) {
    if (!data.groupId && !data.threadId) return;

    const key = data.groupId || data.threadId;
    const normalizedKey = this._normalizeJoinKey(key);
    const isJoinEvent = this._isJoinEvent(event, data);
    const joinEntry = normalizedKey ? this.joiningGroups.get(normalizedKey) : null;

    if (isJoinEvent && normalizedKey && this.joiningGroups.has(normalizedKey)) return;
    if (isJoinEvent && joinEntry?.joining) return;

    if (this.isConnected) {
      if (isJoinEvent) {
        this._markJoiningGroupEntry(normalizedKey || key, {
          data,
          joining: true,
          disconnecting: false,
        });
      }
      this._emitMessage(event, data, onSuccess, onError);
    } else if (!this.messageQueue.has(key)) {
      this.messageQueue.set(key, { event, data, onSuccess, onError });
    }
  }

  leaveGroup(onSuccess = null, onError = null) {
    if (!this.currentGroup) return;

    // Safety: never leave a chat room via leaveGroup — use leaveChat instead
    if (this.currentGroup.type === 'joinChat' || this.currentGroup.threadId) {
      if (onSuccess) onSuccess();
      return;
    }

    const key = this.currentGroup.groupId;
    const normalizedKey = this._normalizeJoinKey(key);
    if (this.joiningGroups.get(normalizedKey)?.disconnecting) return;

    // If the joiningGroups entry for this key belongs to a chat join (joinChat),
    // do NOT overwrite or release it — application-detail's leaveGroup must not
    // destroy the SupportChat's join state for the same processInstanceId.
    const existingEntry = normalizedKey ? this.joiningGroups.get(normalizedKey) : null;
    const existingIsChatJoin =
      existingEntry?.data?.type === 'joinChat' ||
      existingEntry?.event === 'joinChat' ||
      existingEntry?.data?.threadId;
    if (existingIsChatJoin) {
      this.currentGroup = null;
      if (onSuccess) onSuccess();
      return;
    }

    if (!this.isConnected) {
      this._releaseJoiningGroupEntry(key);
      this.currentGroup = null;
      if (onSuccess) onSuccess();
      return;
    }

    this._markJoiningGroupEntry(key, {
      data: this.currentGroup,
      disconnecting: true,
      joining: false,
    });
    this._leaveCurrentGroup(this.currentGroup, onSuccess, onError);
  }

  leaveChat(threadId, onSuccess = null, onError = null) {
    if (!threadId) return;

    const key = threadId.toString().toUpperCase();
    if (this.joiningGroups.get(key)?.disconnecting) return;

    if (!this.isConnected) {
      this._releaseJoiningGroupEntry(key);
      if (onSuccess) onSuccess();
      return;
    }

    const chatData = {
      type: 'leaveChat',
      threadId: key,
      groupId: key,
    };

    this._markJoiningGroupEntry(key, {
      data: chatData,
      disconnecting: true,
      joining: false,
    });
    this._leaveCurrentGroup(chatData, onSuccess, onError);
  }

  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event).add(callback);
  }

  off(event, callback) {
    if (callback) {
      if (this.socket) {
        this.socket.off(event, callback);
      }
      const listeners = this.eventListeners.get(event);
      if (listeners) {
        listeners.delete(callback);
        if (listeners.size === 0) {
          this.eventListeners.delete(event);
        }
      }
    } else {
      const listeners = this.eventListeners.get(event);
      if (listeners && this.socket) {
        listeners.forEach((cb) => this.socket.off(event, cb));
      }
      this.eventListeners.delete(event);
    }
  }

  _emitMessage(event, data, onSuccess, onError) {
    // If socket not connected
    if (!this.socket || !this.socket.connected) {
      // DO NOT queue chat messages when offline to avoid confusing user
      if (event === 'chatMessage') {
        if (onError) onError({ message: 'offline' });
        return;
      }
      // For other events (joins, etc.) keep minimal queuing
      const key = data.groupId || data.threadId;
      if (key && !this.messageQueue.has(key)) {
        this.messageQueue.set(key, { event, data, onSuccess, onError });
      }
      return;
    }

    this.socket.emit(event, data, (ack) => {
      // The backend may ack with one of:
      //   - boolean true  → success
      //   - boolean false → generic failure
      //   - object { ok: true,  ... } → success
      //   - object { ok: false, error_code, description } → typed failure (e.g. 401)
      // `if (ack)` alone is unsafe because `{ ok: false }` is truthy.
      const ackInfo = this._parseAck(ack);

      if (ackInfo.ok) {
        this._handleMessageAck(event, data, onSuccess);
        return;
      }

      this._handleMessageError(event, data);

      if (ackInfo.isAuthError) {
        // Surface as an auth error so the token-refresh hook kicks in and
        // any caller-side retry logic can react accordingly.
        tokenEvents.emit(TOKEN_EVENTS.SOCKET_AUTH_ERROR, {
          error: {
            message: 'unauthorized_emit',
            event,
            statusCode: ackInfo.errorCode,
            description: ackInfo.description,
          },
          timestamp: Date.now(),
          socketService: this,
        });
      }

      if (onError) {
        onError({
          ...data,
          ackError: ackInfo,
          isAuthError: ackInfo.isAuthError,
        });
      }
    });
  }

  /**
   * Normalizes the ack payload returned by the backend into a stable shape.
   * Detects authentication failures (401 / "Unauthorized") so callers can
   * trigger a token refresh + retry instead of dropping the user message.
   */
  _parseAck(ack) {
    if (ack === true) return { ok: true, raw: ack };
    if (ack === false) {
      return { ok: false, isAuthError: false, errorCode: null, description: null, raw: ack };
    }

    if (ack && typeof ack === 'object') {
      const ok = ack.ok === true;
      const errorCode = ack.error_code ?? ack.errorCode ?? null;
      const description = ack.description ?? ack.message ?? null;
      const isAuthError =
        errorCode === 401 || (typeof description === 'string' && /unauthorized/i.test(description));
      return { ok, isAuthError, errorCode, description, raw: ack };
    }

    // Unknown ack shape — treat as success to avoid false negatives.
    return { ok: true, raw: ack };
  }

  _handleMessageAck(event, data, onSuccess) {
    const isJoinEvent = this._isJoinEvent(event, data);

    if (isJoinEvent) {
      // Only update currentGroup for non-chat, non-global groups.
      // Chat rooms are managed separately via leaveChat() and must never
      // overwrite currentGroup — otherwise leaveGroup() from pages like
      // /applications would inadvertently leave the active chat room.
      const isChatJoin = data.type === 'joinChat' || event === 'joinChat';
      if (data.type !== 'all' && !isChatJoin) this.currentGroup = data;
      // Persist ONLY join events to restore after reconnection
      this._markJoiningGroupEntry(data.groupId, {
        data,
        event: data?.type || event,
        joining: false,
        disconnecting: false,
      });
      // Notify UI when joins are confirmed so input can be enabled immediately
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('socketJoined', {
              detail: {
                event: event || data?.type,
                groupId: data?.groupId,
                threadId: data?.threadId,
              },
            }),
          );
        }
      } catch (e) {}
    }

    if (onSuccess) onSuccess(data);
  }

  _handleMessageError(event, data) {
    if (this._isJoinEvent(event, data) || this._isLeaveEvent(event, data)) {
      const key = data?.groupId || data?.threadId;
      this._releaseJoiningGroupEntry(key);
    }
  }

  _leaveCurrentGroup(data, onSuccess, onError) {
    if (!this.socket) return;

    const eventName = data?.type === 'leaveChat' ? 'leaveChat' : 'leaveGroup';

    this.socket.emit(eventName, data, (ok) => {
      if (ok) {
        this.off(data.type);
        this._handleLeaveGroupAck(data, onSuccess);
      } else {
        this._handleMessageError(eventName, data);
        if (onError) onError(data);
      }
    });
  }

  _handleLeaveGroupAck(data, onSuccess) {
    this.currentGroup = null;
    this._releaseJoiningGroupEntry(data.groupId);
    if (onSuccess) onSuccess(data);
  }

  _processQueue() {
    for (const [key, { event, data, onSuccess, onError }] of this.messageQueue) {
      // Skip chat messages to ensure no offline queue for user messages
      if (event === 'chatMessage') {
        this.messageQueue.delete(key);
        continue;
      }
      this._emitMessage(event, data, onSuccess, onError);
      this.messageQueue.delete(key);
    }
  }

  _offAll() {
    if (this.socket) {
      this.eventListeners.forEach((callbacks, event) => {
        if (callbacks && callbacks.forEach) {
          callbacks.forEach((cb) => this.socket.off(event, cb));
        }
      });
    }
    this.eventListeners.clear();
  }

  _leaveAll() {
    if (this.socket) {
      this.joiningGroups.forEach(({ data }) => {
        this._leaveCurrentGroup(data, null, null);
      });
      this.joiningGroups.clear();
    }
  }

  getSocket() {
    return this.socket;
  }

  // Method to verify if socket is properly initialized
  isInitialized() {
    return this.socket && this.initialized;
  }

  /**
   * Public API to emit a message event (e.g. 'chatMessage') with success/error callbacks.
   * Use this instead of calling _emitMessage directly from consumer code.
   */
  sendMessage(event, data, onSuccess, onError) {
    this._emitMessage(event, data, onSuccess, onError);
  }

  /**
   * Public API to release a stuck join state for a given thread/group.
   * Call this from consumer code when a join timeout or error occurs
   * so that subsequent joins to the same thread are not blocked.
   */
  releaseJoinState(threadId) {
    if (!threadId) return;
    this._releaseJoiningGroupEntry(threadId);
  }

  disconnect() {
    if (!this.socket) return;

    this._offAll();
    if (typeof this.socket.offAny === 'function') {
      this.socket.offAny();
    }
    this._leaveAll();
    this.messageQueue.clear();
    this.socket.disconnect();
    this.isConnected = false;
    this.currentGroup = null;
    this.socket = null;
    this.initialized = false;
    this.serverUrl = null;
    this.reconnectAttempts = 0;
    this.isReconnecting = false;
    this._stopHeartbeat();
    if (this._delayedReconnectTimer) {
      clearTimeout(this._delayedReconnectTimer);
      this._delayedReconnectTimer = null;
    }
    if (this.joiningGroupsCleanupInterval) {
      clearInterval(this.joiningGroupsCleanupInterval);
      this.joiningGroupsCleanupInterval = null;
    }
  }

  // Method to reconnect with new token
  reconnectWithNewToken(serverUrl, newToken = null) {
    // Completely disconnect and reconnect
    this.disconnect();
    this.init(serverUrl, newToken);
  }
}

export default SocketService;
